import * as React from "react";
import { createPortal } from "react-dom";
import { Box } from "../../components/ui/box";
import { Text } from "../../components/ui/text";
import {
  useEntitySelector,
  type EntityItemConfig,
  type EntitySelection,
} from "./entity-selector-context";
import { EntitySelectorItem } from "./entity-selector-item";
import { EntitySelectorSectionLabel } from "./entity-selector-layout";
import type { Omittable } from "../../lib/props";

export type EntitySelectorSelectedProps = Omittable<{
  label?: string;
  hideWhenEmpty?: boolean;
  hideBadge?: boolean;
  hideSelectionHighlight?: boolean;
  /** Return an action label per selected item. When provided, the action button appears on hover. */
  getItemActionLabel?: (selection: EntitySelection) => string | undefined;
  /** Called when an item's action button is clicked. */
  onItemAction?: (entity: EntityItemConfig) => void;
  /** Return a secondary action label per selected item. */
  getItemSecondaryActionLabel?: (
    selection: EntitySelection,
  ) => string | undefined;
  /** Called when an item's secondary action button is clicked. */
  onItemSecondaryAction?: (entity: EntityItemConfig) => void;
  children?: React.ReactNode;
  ref?: React.Ref<HTMLDivElement>;
}>;

/**
 * The vertical list of what is currently selected, headed like a pool section
 * so the two read as one list. Mounting it changes the pools beneath: they
 * omit the entities listed here, so an entity appears ONCE in the popup —
 * here, as chosen — rather than here and again as a ticked row further down.
 * Unticking a row here hands it back to its pool. Contrast
 * `EntitySelectorSelectedChips`, a summary strip that leaves the pool whole.
 *
 * The rows are SEATED in the active pool's scroller (the pool registers the
 * seat; see `registerSelectedHost`), so the chosen rows and the pool's rows
 * scroll as one list under one scrollbar — never a capped "Selected" scroller
 * stacked on a second one. With no pool mounted the section renders where it
 * is written.
 */
function EntitySelectorSelected({
  label = "Selected",
  hideWhenEmpty = false,
  hideBadge = false,
  hideSelectionHighlight = false,
  getItemActionLabel,
  onItemAction,
  getItemSecondaryActionLabel,
  onItemSecondaryAction,
  children,
  ref,
}: EntitySelectorSelectedProps) {
  const {
    selections,
    getPool,
    getPoolBadge,
    activePool,
    onOpenChange,
    registerSelectedSection,
    getSelectedHost,
  } = useEntitySelector();

  // Layout effect, not effect: the pools must drop this section's rows in the
  // same paint this section first appears in, or the popup opens with every
  // selection listed twice for one frame.
  React.useLayoutEffect(
    () => registerSelectedSection(),
    [registerSelectedSection],
  );

  const host = getSelectedHost(activePool);
  const pinnedRank = (selection: EntitySelection): number =>
    selection.entity.lead ||
    getPool(selection.poolId)?.pinnedItems?.some((item) => item.id === selection.id)
      ? 0
      : 1;

  if (hideWhenEmpty && selections.length === 0) {
    return null;
  }

  // Seated in the pool, the pool's frame already pays the list area's 6px
  // top gutter; in place, this section is the list's first row block and
  // pays it itself (the `data-slot` tells the pool below it is paid).
  const gutterClass = host ? undefined : "pt-1.5";

  let block: React.ReactElement;
  if (children) {
    block = (
      <Box
        ref={ref}
        data-slot="entity-selector-selected"
        className={gutterClass}
      >
        {children}
      </Box>
    );
  } else {
    // A pool's lead and pinned items lead this section too: both mean
    // "always easy to find", and a chosen one belongs at the top of the
    // chosen run the way an unchosen one heads the pool. Stable, so the rest
    // keep the order they were picked in.
    const ordered = [...selections].sort((a, b) => pinnedRank(a) - pinnedRank(b));
    const items =
      ordered.length > 0 ? (
        ordered.map((selection) => {
          const badge = getPoolBadge(selection.poolId);
          const itemActionLabel =
            getItemActionLabel?.(selection) ?? badge?.label;
          const itemSecondaryActionLabel =
            getItemSecondaryActionLabel?.(selection);
          // A closeOnAction badge is a door (see EntityBadgeConfig): the picker
          // dismisses before the action opens whatever it opens. The section's
          // own handler wins over the badge's pool-carried default.
          const baseAction = onItemAction ?? badge?.onAction;
          const handleAction =
            baseAction && badge?.closeOnAction
              ? (entity: EntityItemConfig) => {
                  onOpenChange(false);
                  baseAction(entity);
                }
              : baseAction;
          return (
            <EntitySelectorItem
              key={selection.id}
              entity={selection.entity}
              poolId={selection.poolId}
              actionLabel={itemActionLabel}
              actionTooltip={badge?.tooltip}
              onAction={handleAction}
              secondaryActionLabel={itemSecondaryActionLabel}
              onSecondaryAction={onItemSecondaryAction}
              showCheckbox
              hideSelectionHighlight={hideSelectionHighlight}
              // The chosen row keeps its pool's rail: a pool that hides its
              // avatar column hides it here too, or ticking a row grows it a
              // glyph its unticked neighbours below never had.
              hideAvatar={getPool(selection.poolId)?.hideAvatars ?? false}
            />
          );
        })
      ) : (
        <Box className="px-2.5" paddingBottom="md">
          <Text size="sm" variant="muted">
            No items selected
          </Text>
        </Box>
      );

    block = (
      <Box
        direction="column"
        ref={ref}
        data-slot="entity-selector-selected"
        className={gutterClass}
      >
        {/* The pool's own section rung — the two sections share one popup, so
            they must share one label treatment and one 10px reading edge. */}
        <EntitySelectorSectionLabel
          label={label}
          count={hideBadge ? undefined : selections.length}
        />
        {selections.length > 0 ? (
          // The chosen rows are a list of their own, headed by the label
          // above: the label is not a member of the list it names, and the
          // pool below seats this block inside its own scroller. The list
          // area's 6px gutter closes the block, so the last chosen row's wash
          // sits as far off the pool below as it does off the popup's sides.
          <Box role="listbox" aria-label={label} className="pb-1.5">
            {items}
          </Box>
        ) : (
          items
        )}
      </Box>
    );
  }

  return host ? createPortal(block, host) : block;
}

EntitySelectorSelected.displayName = "EntitySelectorSelected";

export { EntitySelectorSelected };
